import { useState, useEffect } from "react";
import { Zap, ArrowDown, TrendingUp, Shield, Rocket, Calendar, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { PRESALE_CONFIG, isPresaleActive } from "@/lib/presale";

export function PresaleHero() {
  const { connected } = useWallet();
  const { setVisible } = useWalletModal();
  const [presaleActive, setPresaleActive] = useState(isPresaleActive());

  useEffect(() => {
    setPresaleActive(isPresaleActive());
    const interval = setInterval(() => {
      setPresaleActive(isPresaleActive());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-indigo-900/20 to-violet-900/20" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.3),transparent_50%)]" />

      {/* Floating lightning bolts with animation */}
      <div className="absolute top-20 left-4 md:left-10 opacity-20 animate-pulse">
        <Zap className="w-12 h-12 md:w-16 md:h-16 text-purple-400" fill="currentColor" />
      </div>
      <div className="absolute bottom-20 right-4 md:right-10 opacity-20 animate-pulse" style={{ animationDelay: "1s" }}>
        <Zap className="w-10 h-10 md:w-12 md:h-12 text-violet-400" fill="currentColor" />
      </div>
      <div className="absolute top-1/2 right-4 md:right-20 opacity-10 animate-pulse hidden md:block" style={{ animationDelay: "0.5s" }}>
        <Zap className="w-16 h-16 md:w-20 md:h-20 text-indigo-400" fill="currentColor" />
      </div>
      <div className="absolute top-1/3 left-4 md:left-20 opacity-10 animate-pulse hidden md:block" style={{ animationDelay: "1.5s" }}>
        <Zap className="w-14 h-14 text-purple-300" fill="currentColor" />
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* Logo with enhanced animation */}
        <div className="mb-8 flex justify-center animate-fade-in">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-violet-500 rounded-full blur-2xl opacity-50 animate-pulse group-hover:opacity-75 transition-opacity" />
            <div className="relative bg-gradient-to-br from-purple-600 to-violet-600 p-4 md:p-6 rounded-full transform transition-transform duration-300 group-hover:scale-110">
              <Zap className="w-16 h-16 md:w-24 md:h-24 text-white" fill="white" />
            </div>
          </div>
        </div>

        {/* Title with better typography */}
        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-violet-400 to-indigo-400 bg-clip-text text-transparent animate-fade-in-up">
          $PULSE
        </h1>

        {/* Enhanced subtitle */}
        <p className="text-xl sm:text-2xl md:text-3xl text-gray-200 mb-4 font-semibold animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          Lightning-Fast DeFi on Solana
        </p>
        <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-4 max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          {presaleActive
            ? "Join thousands of early supporters in the $PULSE presale. Built for speed, designed for growth. Get in before the public launch and secure your position in the future of DeFi."
            : "Thank you to all our presale participants! The presale has concluded successfully. Built for speed, designed for growth. Stay tuned for the public launch and the future of DeFi."}
        </p>

        {/* Launch Date */}
        <div className="mb-8 flex items-center justify-center gap-2 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
          <Calendar className="w-5 h-5 text-purple-400" />
          <span className="text-base md:text-lg text-purple-300 font-semibold">
            Launch Date: Thursday, December 11th, 2025
          </span>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <div className="flex flex-col items-center p-4 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <Rocket className="w-6 h-6 md:w-8 md:h-8 text-purple-400 mb-2" />
            <span className="text-xs md:text-sm text-gray-300 font-medium">Fast & Secure</span>
          </div>
          <div className="flex flex-col items-center p-4 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-violet-400 mb-2" />
            <span className="text-xs md:text-sm text-gray-300 font-medium">High Growth</span>
          </div>
          <div className="flex flex-col items-center p-4 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <Shield className="w-6 h-6 md:w-8 md:h-8 text-indigo-400 mb-2" />
            <span className="text-xs md:text-sm text-gray-300 font-medium">Secure</span>
          </div>
          <div className="flex flex-col items-center p-4 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <Zap className="w-6 h-6 md:w-8 md:h-8 text-purple-300 mb-2" />
            <span className="text-xs md:text-sm text-gray-300 font-medium">Instant</span>
          </div>
        </div>

        {/* CTA Button with enhanced styling */}
        {!presaleActive ? (
          <div className="animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <Button
              size="lg"
              disabled
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg shadow-gray-500/50 opacity-75 cursor-not-allowed"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Presale Completed
            </Button>
          </div>
        ) : !connected ? (
          <div className="animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <Button
              onClick={() => setVisible(true)}
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg shadow-purple-500/50 transform transition-all duration-300 hover:scale-105 hover:shadow-purple-500/70"
            >
              <Zap className="w-5 h-5 mr-2" />
              Connect Wallet to Participate
            </Button>
          </div>
        ) : (
          <div className="animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg shadow-purple-500/50 transform transition-all duration-300 hover:scale-105 hover:shadow-purple-500/70"
              onClick={() => {
                document.getElementById("presale-purchase")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start Purchasing
            </Button>
          </div>
        )}

        {/* Scroll indicator */}
        <div className="mt-16 animate-bounce">
          <ArrowDown className="w-6 h-6 text-purple-400 mx-auto opacity-60" />
        </div>
      </div>
    </div>
  );
}
